import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { theme } from "../theme";
import { useApp } from "../AppContext";
import Sidebar, { SIDEBAR_WIDTH, OKRAJ } from "./Sidebar";
import TopBar from "./TopBar";
import DetailModal from "../components/DetailModal";
import AddProductModal from "../components/AddProductModal";
import GroupModal from "../components/GroupModal";
import SpotlightTour from "../components/SpotlightTour";
import OnboardingModal from "../screens/OnboardingModal";

const BREAKPOINT = 900;
// Jeden vodorovný rytmus pro hlavičku i obsah, ať sedí na stejné mřížce.
// Maximální šířka obsahu je pryč — na širokém monitoru zbývala vpravo
// prázdná plocha, kdežto v návrhu obsah vyplňuje celou plochu vedle sidebaru.
const PADDING_X = 28;
const PADDING_X_COMPACT = 16;

// sbalený sidebar pod 900px
function useCompact() {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && window.innerWidth < BREAKPOINT);
  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return compact;
}

export default function AppShell() {
  const compact = useCompact();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    token, license, loadProducts, setProducts, setGroups, groups, loadGroups,
    selectedProduct, setSelectedProduct,
    showAddModal, setShowAddModal,
    showGroupModal, setShowGroupModal,
    showTour, finishTour,
    needsOnboarding, completeOnboarding,
    products,
    redirectToSettings, clearSettingsRedirect,
  } = useApp();

  // licence vypršela / není aktivní → rovnou na Nastavení (dřív se otevřel SettingsModal)
  useEffect(() => {
    if (!redirectToSettings) return;
    clearSettingsRedirect();
    navigate("/nastaveni");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToSettings]);

  // při přechodu na jinou stránku zavři mobilní menu
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => { if (!compact) setMenuOpen(false); }, [compact]);

  return (
    <div style={{ minHeight: "100vh", background: theme.bgSoft, fontFamily: theme.font, display: "flex" }}>
      {/* sidebar — fixní vlevo (desktop) */}
      {!compact && (
        <div style={{ position: "fixed", top: OKRAJ, left: OKRAJ, bottom: OKRAJ, width: SIDEBAR_WIDTH, zIndex: 45 }}>
          <Sidebar />
        </div>
      )}

      {/* sidebar jako overlay (mobil) */}
      {compact && menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, background: "#0f172a55", backdropFilter: "blur(2px)", zIndex: 60 }} />
          <div className="slideIn" style={{ position: "fixed", top: OKRAJ, left: OKRAJ, bottom: OKRAJ, zIndex: 61 }}>
            <Sidebar onNavigate={() => setMenuOpen(false)} />
          </div>
        </>
      )}

      {/* obsah */}
      <div style={{
        flex: 1, minWidth: 0, marginLeft: compact ? 0 : SIDEBAR_WIDTH + OKRAJ * 2,
        display: "flex", flexDirection: "column", minHeight: "100vh",
      }}>
        <TopBar compact={compact} onOpenMenu={() => setMenuOpen(true)} paddingX={compact ? PADDING_X_COMPACT : PADDING_X} />
        <main style={{
          flex: 1,
          padding: compact
            ? `4px ${PADDING_X_COMPACT}px 56px`
            : `4px ${PADDING_X}px 64px`,
        }}>
          <Outlet />
        </main>
      </div>

      {/* globální modaly */}
      {selectedProduct && <DetailModal product={selectedProduct} token={token} groups={groups} vychoziHranice={license?.notify_threshold} onGroupChange={(id, gid) => { setProducts(prev => prev.map(p => p.id === id ? { ...p, group_id: gid } : p)); loadProducts(); loadGroups(); }} onClose={() => setSelectedProduct(null)} />}
      {showTour && <SpotlightTour onFinish={finishTour} hasProducts={products.length > 0} />}
      {showGroupModal && <GroupModal token={token} onClose={() => setShowGroupModal(false)} onSave={g => { setGroups(prev => [...prev, { ...g, product_count: 0 }]); }} />}
      {showAddModal && <AddProductModal token={token} groups={groups} onClose={() => setShowAddModal(false)} onAdd={p => { setProducts(prev => [...prev, p]); loadProducts(); loadGroups(); }} />}
      {needsOnboarding && <OnboardingModal token={token} license={license} onComplete={completeOnboarding} />}
    </div>
  );
}
